import React, { useState, useMemo } from 'react';
import { Button, Dropdown, Input, Space, Empty, Spin, Popconfirm } from 'antd';
import {
  BookOutlined,
  SaveOutlined,
  DeleteOutlined,
  DownOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../hooks/useUser';
import {
  useSavedSearches,
  useCreateSavedSearch,
  useDeleteSavedSearch,
} from '../../hooks/useSavedSearches';

/**
 * Compare two URLs by their search params (ignoring order)
 */
const urlsMatch = (url1, url2) => {
  try {
    const parsed1 = new URL(url1);
    const parsed2 = new URL(url2);

    // Compare pathname
    if (parsed1.pathname !== parsed2.pathname) return false;

    // Compare search params (order-independent)
    const params1 = new URLSearchParams(parsed1.search);
    const params2 = new URLSearchParams(parsed2.search);

    // Check same number of params
    if ([...params1.keys()].length !== [...params2.keys()].length) return false;

    // Check each param matches
    for (const [key, value] of params1) {
      if (params2.get(key) !== value) return false;
    }

    return true;
  } catch {
    // Fallback to simple comparison
    return url1 === url2;
  }
};

/**
 * Save Search Button with dropdown for saved searches
 * Shows only when user is authenticated
 * @param {string} searchType - Type of search (active_substance, commercial_product, treatment, risk_calculator)
 * @param {Function} getCurrentUrl - Function to get current URL
 * @param {Function} onApplySearch - Optional callback when applying a saved search
 * @param {string} className - Optional className for the button
 */
const SaveSearchButton = ({
  searchType,
  getCurrentUrl,
  onApplySearch,
  className,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useUser();
  const [searchName, setSearchName] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const { data: savedSearches = [], isLoading } = useSavedSearches(searchType, {
    enabled: isAuthenticated,
  });

  const createMutation = useCreateSavedSearch();
  const deleteMutation = useDeleteSavedSearch();

  // Find which saved search matches the current URL
  const activeSearchId = useMemo(() => {
    const currentUrl = getCurrentUrl ? getCurrentUrl() : window.location.href;
    const matchingSearch = savedSearches.find((search) =>
      urlsMatch(search.search_url, currentUrl)
    );
    return matchingSearch?.id || null;
  }, [savedSearches, getCurrentUrl]);

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const handleSave = async () => {
    if (!searchName.trim()) return;

    const currentUrl = getCurrentUrl ? getCurrentUrl() : window.location.href;

    await createMutation.mutateAsync({
      name: searchName.trim(),
      search_type: searchType,
      search_url: currentUrl,
    });

    setSearchName('');
  };

  const handleApply = (search) => {
    if (onApplySearch) {
      onApplySearch(search.search_url);
    } else {
      window.location.href = search.search_url;
    }
    setDropdownOpen(false);
  };

  const handleDelete = async (e, searchId) => {
    e.stopPropagation();
    await deleteMutation.mutateAsync(searchId);
  };

  const dropdownContent = (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 w-80 max-h-96 overflow-hidden">
      {/* Save new search section */}
      <div className="p-3 border-b border-gray-100">
        <p className="text-sm font-medium text-gray-700 mb-2">
          {t('common|Save current search')}
        </p>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder={t('common|Enter search name')}
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onPressEnter={handleSave}
            disabled={createMutation.isPending}
          />
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={createMutation.isPending}
            disabled={!searchName.trim()}
          />
        </Space.Compact>
      </div>

      {/* Saved searches list */}
      <div className="max-h-64 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-center">
            <Spin size="small" />
          </div>
        ) : savedSearches.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('common|No saved searches')}
            className="py-4"
          />
        ) : (
          <>
            <div className="px-3 py-2 bg-gray-50">
              <p className="text-xs font-medium text-gray-500 uppercase">
                {t('common|My saved searches')} ({savedSearches.length})
              </p>
            </div>
            {savedSearches.map((search) => {
              const isActive = search.id === activeSearchId;
              return (
                <div
                  key={search.id}
                  className={`flex items-center justify-between px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 ${
                    isActive ? 'bg-green-50 border-l-2 border-l-green-500' : ''
                  }`}
                  onClick={() => handleApply(search)}
                >
                  <div className="flex items-center flex-1 mr-2 min-w-0">
                    {isActive && (
                      <CheckOutlined className="text-green-500 mr-2 flex-shrink-0" />
                    )}
                    <span
                      className={`text-sm truncate ${isActive ? 'text-green-700 font-medium' : 'text-gray-700'}`}
                    >
                      {search.name}
                    </span>
                  </div>
                  <Popconfirm
                    title={t('common|Delete')}
                    description={t(
                      'common|Are you sure you want to delete this search?'
                    )}
                    onConfirm={(e) => {
                      e?.stopPropagation?.();
                      handleDelete(e, search.id);
                    }}
                    onCancel={(e) => {
                      e?.stopPropagation?.();
                    }}
                    onPopupClick={(e) => {
                      e?.stopPropagation?.();
                    }}
                    okText={t('common|Yes')}
                    cancelText={t('common|No')}
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                      loading={deleteMutation.isPending}
                    />
                  </Popconfirm>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      dropdownRender={() => dropdownContent}
      trigger={['click']}
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
      placement="bottomRight"
    >
      <Button
        size="large"
        className={
          className ||
          'w-full sm:w-auto text-sm sm:text-base py-2.5 px-4 sm:py-2 sm:px-3'
        }
        icon={<BookOutlined />}
      >
        {t('common|My searches')}
        {savedSearches.length > 0 && (
          <span className="ml-1 bg-green-500 text-white text-xs rounded-full px-1.5 py-0.5">
            {savedSearches.length}
          </span>
        )}
        <DownOutlined className="ml-1 text-xs" />
      </Button>
    </Dropdown>
  );
};

export default SaveSearchButton;
